import { v5 as uuidv5 } from 'uuid'
import { z } from 'zod'

const MAX_DOCUMENT_FAVORITES = 100
export const DOCUMENT_FAVORITES_TYPE = 'power-document-favorites'

const favoriteReferenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('document'), document_id: z.string().min(1) }),
  z.object({ kind: z.literal('cmms_record'), entry_id: z.string().min(1) }),
  z.object({
    kind: z.literal('legacy_system_document'),
    entry_id: z.string().min(1),
  }),
])

const documentFavoriteSchema = z.object({
  reference: favoriteReferenceSchema,
  favorited_at: z.number().int().nonnegative(),
})

export const documentFavoritesDataSchema = z.object({
  user_id: z.string().min(1),
  favorites: z
    .array(documentFavoriteSchema)
    .max(MAX_DOCUMENT_FAVORITES)
    .default([]),
})

export type FavoriteReference = z.infer<typeof favoriteReferenceSchema>
export type DocumentFavorite = z.infer<typeof documentFavoriteSchema>
export type DocumentFavoritesData = z.infer<typeof documentFavoritesDataSchema>
export type FavoriteOperation =
  | { type: 'add'; favorite: DocumentFavorite }
  | { type: 'remove'; reference: FavoriteReference }

export type FavoritesScope = {
  userId: string
  siteId: string
  orgId: string
}

export function documentFavoritesEntryId(userId: string, siteId: string) {
  return uuidv5(
    JSON.stringify([DOCUMENT_FAVORITES_TYPE, userId, siteId]),
    uuidv5.URL
  )
}

export function favoriteReferenceId(reference: FavoriteReference) {
  return reference.kind === 'document'
    ? reference.document_id
    : reference.entry_id
}

function referenceKey(reference: FavoriteReference) {
  return JSON.stringify([reference.kind, favoriteReferenceId(reference)])
}

export function normalizeFavorites(favorites: DocumentFavorite[]) {
  const newestByReference = new Map<string, DocumentFavorite>()
  for (const favorite of favorites) {
    const key = referenceKey(favorite.reference)
    const existing = newestByReference.get(key)
    if (!existing || favorite.favorited_at > existing.favorited_at)
      newestByReference.set(key, favorite)
  }
  return [...newestByReference.values()]
    .sort(
      (a, b) =>
        b.favorited_at - a.favorited_at ||
        referenceKey(a.reference).localeCompare(referenceKey(b.reference))
    )
    .slice(0, MAX_DOCUMENT_FAVORITES)
}

export function emptyDocumentFavorites(userId: string): DocumentFavoritesData {
  return { user_id: userId, favorites: [] }
}

export function applyFavoriteOperation(
  data: DocumentFavoritesData,
  operation: FavoriteOperation
): DocumentFavoritesData {
  const reference =
    operation.type === 'add'
      ? operation.favorite.reference
      : operation.reference
  const key = referenceKey(reference)
  const remaining = data.favorites.filter(
    (favorite) => referenceKey(favorite.reference) !== key
  )
  return {
    ...data,
    favorites:
      operation.type === 'add'
        ? normalizeFavorites([operation.favorite, ...remaining])
        : remaining,
  }
}
